import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import AlertDialog from "@/components/AlertDialog";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import {
  useEducationalResources,
  useEducationalResourceDeleteOne,
} from "@/hooks/useEducationalResources";
import {
  EducationalResourceStatusEnum,
  EducationalResourceTypeEnum,
} from "@/domain/educational-resource.enum";
import Select from "@/components/Select";
import { EducationalResource } from "@/domain/educational-resource.interface";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import { EducationalResourceModal } from "@/ui/modals/educationalResources/EducationalResourceModal";
import ActionMenu from "@/components/ActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";

interface Filters {
  sortField: string;
  sortOrder: number;
  search: string;
  pageNumber: number;
  pageSize: number;
  status?: string[];
  type?: string[];
  categoryId?: string;
}

const EducationalResourcesPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t: tCommon } = useTranslation("common");

  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();

  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const createAuditLog = useCreateAuditlog();
  const deleteEducationalResourceOne = useEducationalResourceDeleteOne();

  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle("Educational Resources");
  }, []);

  const statusOptions = useMemo(() => {
    return Object.values(EducationalResourceStatusEnum).reduce(
      (acc, status) => {
        acc[status] = tCommon(`enums.${status}`);
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [tCommon]);

  const typeOptions = useMemo(() => {
    return Object.values(EducationalResourceTypeEnum).reduce(
      (acc, type) => {
        acc[type] = type;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, []);

  const [isEducationalResourceModalOpen, setIsEducationalResourceModalOpen] =
    useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedEducationalResource, setSelectedEducationalResource] =
    useState<EducationalResource | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    educationalResource: EducationalResource | null;
  }>({
    open: false,
    educationalResource: null,
  });

  const [filters, setFilters] = useState<Filters>({
    sortField: "priority",
    sortOrder: 1,
    search: "",
    pageNumber: 0,
    pageSize: 10,
    status: [EducationalResourceStatusEnum.ACTIVE],
    type: [],
    categoryId: undefined,
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const {
    educationalResources,
    totalCount,
    isLoadingEducationalResources,
    refetchEducationalResources,
  } = useEducationalResources({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: filters.search,
    status: filters.status,
    type: filters.type,
    categoryId: filters.categoryId,
  });

  const rows = useMemo(() => {
    if (!educationalResources || educationalResources.length === 0) return [];

    return educationalResources.map((resource, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedEducationalResource(resource);
            setModalMode("edit");
            setIsEducationalResourceModalOpen(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setShowDeleteModal({ open: true, educationalResource: resource });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const englishContent = resource.languages?.find(
        (c) => c.language === "en",
      );
      const title =
        englishContent?.title || resource.languages?.[0]?.title || "-";

      const row = createDataRow(resource._id || index, {
        title: <Typography variant="bodySmall">{title}</Typography>,
        type: <Typography variant="bodyMedium">{resource.type}</Typography>,
        priority: (
          <Typography variant="bodyMedium">{resource.priority}</Typography>
        ),
        status: (
          <Chip
            sx={{
              bgcolor:
                resource.status === EducationalResourceStatusEnum.ACTIVE
                  ? theme.palette.brand.light
                  : theme.palette.grey[300],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                resource.status === EducationalResourceStatusEnum.ACTIVE
                  ? theme.palette.brand.main
                  : theme.palette.grey[500],
            }}
            label={
              <Typography variant="labelSmall">
                {statusOptions[resource.status] || resource.status}
              </Typography>
            }
            size="medium"
          />
        ),
        actions: <ActionMenu id={resource._id} options={actionMenu} />,
      });

      return row;
    });
  }, [educationalResources, statusOptions, theme]);

  const headCells: HeadCell[] = [
    {
      id: "title",
      isSortable: false,
      disablePadding: false,
      label: "Title",
      align: "left",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
    },
    {
      id: "priority",
      isSortable: true,
      disablePadding: false,
      label: "Priority",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
    },
  ];

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EDUCATIONAL_RESOURCES,
        option: OPTIONS.VIEW,
        actionCode: ACTION_CODE.EDUCATIONAL_RESOURCES_VIEW,
        action: "When entering",
        detail: "Viewed the list of educational resources",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  const previousSearchRef = React.useRef<string>("");
  useEffect(() => {
    if (userInfo) {
      if (
        searchDebounce !== previousSearchRef.current &&
        searchDebounce &&
        searchDebounce.trim().length > 0
      ) {
        previousSearchRef.current = searchDebounce;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.SEARCH,
          actionCode: ACTION_CODE.EDUCATIONAL_RESOURCES_SEARCH,
          action: "When searching",
          detail: "Searched within the list of educational resources",
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }
    }
  }, [userInfo, searchDebounce, createAuditLog]);

  const handleDelete = async () => {
    if (!showDeleteModal.educationalResource) return;

    const resourceId = showDeleteModal.educationalResource._id;

    deleteEducationalResourceOne.mutate(resourceId, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.DELETE,
          actionCode: ACTION_CODE.EDUCATIONAL_RESOURCES_DELETE,
          action: "When deleting",
          detail: `Delete educational resource ${resourceId}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });

        refetchEducationalResources();
        setShowDeleteModal({ open: false, educationalResource: null });
        showSnackBar("Educational Resource deleted successfully", "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, educationalResource: null });
        showSnackBar(
          error?.response?.data?.message ||
            "Failed to delete educational resource",
          "error",
        );
      },
    });
  };

  console.log("educationalResources", educationalResources);

  return (
    <Box>
      {/* Top Bar - Header with title and buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
        }}
      >
        <Typography variant="headlineMedium">Educational Resources</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isLoadingEducationalResources ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedEducationalResource(null);
                  setModalMode("create");
                  setIsEducationalResourceModalOpen(true);
                }}
              >
                ADD
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search, Type, and Status */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {
            <Box
              sx={{
                flex: { xs: "none", md: "1 1 auto" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 200px" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    type: Array.isArray(value) ? value : [value],
                    pageNumber: 0,
                  }));
                }}
                data={Object.values(EducationalResourceTypeEnum).map(
                  (type) => ({
                    key: type,
                    value: type,
                  }),
                )}
                value={filters.type || []}
                label="Type"
                id="educational-resources-type"
                width="100%"
                multiple
              />
            </Box>
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 200px" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    status: Array.isArray(value) ? value : [value],
                    pageNumber: 0,
                  }));
                }}
                data={[
                  {
                    key: EducationalResourceStatusEnum.ACTIVE,
                    value: tCommon(
                      `enums.${EducationalResourceStatusEnum.ACTIVE}`,
                    ),
                  },
                  {
                    key: EducationalResourceStatusEnum.INACTIVE,
                    value: tCommon(
                      `enums.${EducationalResourceStatusEnum.INACTIVE}`,
                    ),
                  },
                ]}
                value={filters.status || []}
                label="Status"
                id="educational-resources-status"
                width="100%"
                multiple
              />
            </Box>
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={totalCount}
            isLoading={isLoadingEducationalResources}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
            messageEmptyData="No items to show"
          />
        </CardContent>
      </Card>

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title="Delete Educational Resource"
          description="Are you sure you want to delete this educational resource?"
          onCancel={() => {
            setShowDeleteModal({ open: false, educationalResource: null });
          }}
          onConfirm={handleDelete}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}

      {/* Educational Resource Modal */}
      <EducationalResourceModal
        open={isEducationalResourceModalOpen}
        mode={modalMode}
        educationalResource={selectedEducationalResource}
        onClose={() => {
          setIsEducationalResourceModalOpen(false);
          setSelectedEducationalResource(null);
        }}
        onSuccess={() => {
          refetchEducationalResources();
        }}
        canAddEducationalResource={true}
        canEditEducationalResource={true}
      />
    </Box>
  );
};

export default EducationalResourcesPage;
